import i18n from "../../../i18n";import { Trans } from "react-i18next";import { useState } from "react";
import { Avatar } from "./PersonCard";
import { PhoneIcon, MailIcon, CopyIcon, CheckIcon, TargetIcon } from "./Icons";

// =============================================================================
// PersonPopover.jsx — Screen 2 in the PRD ("Person Detail Popover").
// Not a route — it's a modal opened from a card/row click on either
// OrgChartPage.jsx or UnassignedPage.jsx. Both pages hold the currently-open
// person in a `popPerson` state variable and render this component when
// it's non-null.
// =============================================================================

// ── ContactRow ──
// One line in the popover body (phone or email). Per the PRD, a row with
// no value is hidden completely rather than shown greyed-out/disabled —
// that's the `if (!value) return null;` below.
function ContactRow({ icon, label, value, href, copied, onCopy }) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-3 px-3 py-2.5 rounded-[10px] hover:bg-slate-50">
      <a className="flex items-center gap-3 flex-1 min-w-0 no-underline text-inherit" href={href}>
        <span className="w-[34px] h-[34px] rounded-lg bg-[#EEF2FF] text-primary flex items-center justify-center shrink-0">
          {icon}
        </span>
        <span style={{ minWidth: 0 }}>
          <p className="m-0 text-[11px] font-semibold text-slate-400 uppercase tracking-[0.05em]">{label}</p>
          <p className="mt-0.5 mb-0 text-[13px] font-medium text-foreground overflow-hidden text-ellipsis whitespace-nowrap">
            {value}
          </p>
        </span>
      </a>
      <button
        className={
        "bg-transparent border rounded-lg p-[7px] cursor-pointer flex items-center shrink-0 transition-colors font-[inherit] " + (
        copied ? "text-[#16A34A] border-[#16A34A]" : "text-muted-foreground border-slate-200 hover:text-primary hover:border-border")
        }
        aria-label={`Copy ${label.toLowerCase()}`}
        onClick={onCopy}>
        
        {copied ? <CheckIcon /> : <CopyIcon />}
      </button>
    </div>);

}

// -----------------------------------------------------------------------
// PersonPopover — the modal itself.
// Compact centred card on desktop and mobile alike; clicking the dark
// overlay or the ✕ closes it.
//
// Props:
//   person        — the employee to show (component renders nothing if null)
//   isUnassigned  — true when opened for someone with no manager assigned;
//                    shows the extra "no manager assigned" note at the bottom
//   onClose       — called to dismiss the popover
//   onViewOrgChart — optional; when passed, shows a "View Org Chart" button
//                    that re-centres the chart on this person (so anyone can
//                    browse anyone else's reporting line, not just their own)
// -----------------------------------------------------------------------
export default function PersonPopover({ person, isUnassigned = false, onClose, onViewOrgChart }) {
  // Tracks which field ("phone" or "email") was just copied, so we can
  // briefly swap that row's copy icon to a checkmark. Reset to null after
  // 1.5s by the setTimeout in copy() below.
  const [copiedField, setCopiedField] = useState(null);

  if (!person) return null;

  // Copies `value` to the clipboard and flips the "just copied" state for
  // this field. Falls back to the old textarea+execCommand trick if the
  // Clipboard API isn't available (e.g. non-HTTPS dev context).
  const copy = async (field, value) => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = value;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 1500);
  };

  return (
    <div
      className="fixed inset-0 bg-[rgba(15,23,42,0.30)] flex items-center justify-center p-6 z-[1000] animate-in fade-in duration-150"
      onClick={onClose}>
      
      <div
        className="bg-white rounded-[14px] w-full max-w-[380px] shadow-[0_20px_40px_rgba(0,0,0,0.18)] overflow-hidden"
        onClick={(e) => e.stopPropagation()}>
        
        <div className="flex items-center gap-3.5 px-5 pt-5 pb-4 border-b border-slate-200">
          <Avatar person={person} size="lg" />
          <div className="flex-1 min-w-0">
            <p className="m-0 text-base font-bold text-foreground">{person.name}</p>
            <p className="mt-[3px] mb-0 text-[13px] font-medium text-accent">{person.designation}</p>
            <p className="mt-[3px] mb-0 text-xs text-muted-foreground">{person.department}</p>
          </div>
          <button
            className="self-start bg-transparent border-none text-lg leading-none text-muted-foreground cursor-pointer p-1 shrink-0 hover:text-foreground"
            onClick={onClose}
            aria-label={i18n.t("formActions.close")}>
            
            ✕
          </button>
        </div>

        <div className="px-2 pt-2 pb-3">
          <ContactRow
            icon={<PhoneIcon />}
            label="Phone"
            value={person.phone}
            href={`tel:${person.phone}`}
            copied={copiedField === "phone"}
            onCopy={() => copy("phone", person.phone)} />
          
          <ContactRow
            icon={<MailIcon />}
            label="Email"
            value={person.email}
            href={`mailto:${person.email}`}
            copied={copiedField === "email"}
            onCopy={() => copy("email", person.email)} />
          

          {onViewOrgChart &&
          <button
            type="button"
            className="flex items-center justify-center gap-2 mx-3 mt-1 rounded-lg border border-border bg-white px-3.5 py-2.5 text-[13px] font-semibold text-primary cursor-pointer hover:bg-slate-50 transition-colors"
            style={{ width: "calc(100% - 24px)" }}
            onClick={() => onViewOrgChart(person)}>
            
              <TargetIcon />
              <Trans i18nKey="formActions.viewOrgChart" />
            </button>
          }

          {isUnassigned &&
          <div className="mx-3 mt-1 mb-2 bg-[#FEF3C7] rounded-lg px-3.5 py-2.5 text-xs text-[#92400E] leading-relaxed">
              <Trans i18nKey="orgCharts.noManagerAssignedSetTheManagerOnThis" />
            
          </div>
          }
        </div>
      </div>
    </div>);

}
